import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  CircularProgress,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  TextField,
  InputAdornment,
  Chip,
  Fade,
  Divider,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import { Link } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import UpdateIcon from "@mui/icons-material/Update";
import LaunchIcon from "@mui/icons-material/Launch";
import BookmarkIcon from "@mui/icons-material/Bookmark";

const GSTUpdates = () => {
  const [updates, setUpdates] = useState([]);
  const [filteredUpdates, setFilteredUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const fetchUpdates = async () => {
      try {
        const response = await axios.get(
          "https://api.getyoursolution.in/gstapi.php"
        );
        if (response.data.status === "success") {
          const updateData = response.data.data;
          setUpdates(updateData);
          setFilteredUpdates(updateData);
        }
      } catch (error) {
        console.error("Error fetching GST updates:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUpdates();
  }, []);

  // Filter updates based on search query
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredUpdates(updates);
    } else {
      const filtered = updates.filter(
        (update) =>
          update.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          update.content.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredUpdates(filtered);
    }
  }, [searchQuery, updates]);

  // Format date function - adds a placeholder as the API doesn't provide dates
  const formatDate = (id) => {
    // Using the update ID to generate a semi-realistic date
    // In a real scenario, you'd use the actual date from the API
    const today = new Date();
    const daysAgo = id % 30; // Use ID to generate a random number of days ago
    const date = new Date(today);
    date.setDate(today.getDate() - daysAgo);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const renderHeroSection = () => (
    <Box
      component={Paper}
      elevation={0}
      sx={{
        bgcolor: "primary.main",
        color: "white",
        py: { xs: 6, md: 10 },
        borderRadius: 0,
        backgroundImage: "linear-gradient(135deg, #1976d2 0%, #0a4a94 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            maxWidth: "800px",
            mx: "auto",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            position: "relative",
            zIndex: 2,
          }}
        >
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            className="font-heading"
            sx={{
              fontWeight: 900,
              fontSize: { xs: "2rem", md: "3.5rem" },
              letterSpacing: "-0.02em",
              textAlign: "center",
              textShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            Latest GST News & Updates
          </Typography>
          <Typography
            variant="h5"
            className="font-sans"
            sx={{
              mb: 4,
              lineHeight: 1.5,
              fontSize: { xs: "1rem", md: "1.25rem" },
              fontWeight: 400,
              letterSpacing: "0.01em",
              textAlign: "center",
              maxWidth: "700px",
              opacity: 0.9,
            }}
          >
            Stay informed with real-time access to important GST updates,
            legal orders, and daily cause lists from the GST and more — all in
            one place.
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            <Button
              component={Link}
              to="/contact"
              variant="contained"
              color="secondary"
              size="large"
              sx={{
                fontWeight: 600,
                px: 4,
                py: 1.5,
                boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                "&:hover": {
                  boxShadow: "0 6px 8px rgba(0,0,0,0.15)",
                  transform: "translateY(-2px)",
                },
                transition: "all 0.2s ease",
              }}
            >
              Schedule a Consultation
            </Button>
            <Button
              component={Link}
              to="/about"
              variant="outlined"
              color="inherit"
              size="large"
              sx={{
                fontWeight: 600,
                px: 4,
                py: 1.5,
                borderWidth: 2,
                "&:hover": {
                  borderWidth: 2,
                  bgcolor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              Learn More About Us
            </Button>
          </Box>
        </Box>
      </Container>
      {/* Decorative background elements */}
      <Box
        sx={{
          position: "absolute",
          width: "300px",
          height: "300px",
          borderRadius: "50%",
          background: "rgba(255,255,255,0.05)",
          top: "-50px",
          right: "-100px",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          width: "200px",
          height: "200px",
          borderRadius: "50%",
          background: "rgba(255,255,255,0.08)",
          bottom: "-50px",
          left: "10%",
        }}
      />
    </Box>
  );

  if (loading) {
    return (
      <>
        {renderHeroSection()}
        <Container sx={{ py: 8, textAlign: "center" }}>
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" sx={{ mt: 3, fontWeight: 500, color: "text.secondary" }}>
            Loading GST Updates...
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Please wait while we fetch the latest updates
          </Typography>
        </Container>
      </>
    );
  }

  return (
    <>
      {renderHeroSection()}
      
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h3"
            gutterBottom
            sx={{
              fontWeight: 800,
              fontSize: { xs: "1.75rem", md: "2.5rem" },
              textAlign: "center",
              color: "primary.main",
              mb: 3,
            }}
          >
            Latest GST Updates
          </Typography>
          
          <Box 
            sx={{ 
              maxWidth: "600px", 
              mx: "auto", 
              mb: 5,
              px: 2 
            }}
          >
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search updates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                sx: { 
                  borderRadius: 2,
                  bgcolor: "background.paper",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0,0,0,0.1)',
                  }
                }
              }}
            />
          </Box>

          {filteredUpdates.length === 0 && (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                No updates found matching your search.
              </Typography>
              <Button 
                variant="text" 
                color="primary"
                onClick={() => setSearchQuery("")}
                sx={{ mt: 2 }}
              >
                Clear search
              </Button>
            </Box>
          )}
          
          <Grid container spacing={3}>
            {filteredUpdates.map((update, index) => (
              <Grid item xs={12} key={update.id}>
                <Card 
                  sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
                      transform: "translateY(-5px)",
                    },
                    borderLeft: "5px solid",
                    borderColor: "primary.main",
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, md: 4 } }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 1, mb: 2 }}>
                      <Chip 
                        icon={<UpdateIcon fontSize="small" />}
                        label={formatDate(update.id)}
                        size="small"
                        sx={{ 
                          fontWeight: 500, 
                          bgcolor: "primary.light", 
                          color: "primary.contrastText",
                          mb: 2
                        }} 
                      />
                      <Chip 
                        icon={<BookmarkIcon fontSize="small" />}
                        label="GST Update"
                        size="small"
                        color="secondary"
                        variant="outlined"
                        sx={{ fontWeight: 500 }}
                      />
                    </Box>
                    
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        mb: 2,
                        color: "text.primary",
                        fontSize: { xs: "1.25rem", md: "1.5rem" },
                        lineHeight: 1.3,
                      }}
                    >
                      {update.title}
                    </Typography>
                    
                    <Divider sx={{ mb: 2.5, opacity: 0.6 }} />
                    
                    <Typography
                      variant="body1"
                      sx={{ 
                        color: "text.secondary", 
                        textAlign: { xs: "left", md: "justify" },
                        lineHeight: 1.7,
                        mb: 3,
                        fontSize: { xs: "0.95rem", md: "1rem" }
                      }}
                      dangerouslySetInnerHTML={{ __html: update.content }}
                    />
                  </CardContent>
                  
                  <CardActions sx={{ px: { xs: 2, md: 4 }, pb: 3 }}>
                    <Button
                      component="a"
                      href={`https://www.gst.gov.in/newsandupdates/read/${update.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="contained"
                      color="primary"
                      endIcon={<LaunchIcon />}
                      sx={{ 
                        fontWeight: 600,
                        textTransform: "none",
                        borderRadius: 2,
                        px: 3
                      }}
                    >
                      Read Full Update
                    </Button>
                    
                    <Button
                      component={Link}
                      to="/contact"
                      variant="text"
                      color="secondary"
                      sx={{ 
                        ml: 2, 
                        fontWeight: 600,
                        textTransform: "none"
                      }}
                    >
                      Need Help Understanding This?
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </>
  );
};

export default GSTUpdates;
